<template>
  <View direction="column" gap="lg" data-testid="self-guided-access">
    <FormField label="How do renters get in?" direction="column">
      <RadioButtonGroup direction="column">
        <RadioCard
          v-for="option in accessMethodOptions"
          :key="option.value"
          :name="radioGroupName"
          :value="option.value"
          v-model="method"
          :data-testid="`access-method-${option.testId}`"
        >
          <View direction="row" gap="sm" align="center">
            <IconTile :icon="option.icon" size="small" :color="option.color" />
            <View direction="column" gap="xs">
              <Text variant="label-medium">{{ option.title }}</Text>
              <Text variant="body-medium" color="secondary">{{ option.description }}</Text>
            </View>
          </View>
        </RadioCard>
      </RadioButtonGroup>
    </FormField>

    <Hint v-if="!method">Pick one so we know what to send renters when they book a tour.</Hint>

    <Card v-if="method === SelfGuidedAccessMethod.CODEBOX" size="small" data-testid="codebox-panel">
      <View direction="column" gap="md">
        <Text v-if="integrationQuery.isPending.value" variant="body-medium" color="secondary">
          Checking your CodeBox account…
        </Text>

        <template v-else-if="!isConnected">
          <View direction="column" gap="xs">
            <Text variant="label-medium">Connect your CodeBox account</Text>
            <Text variant="body-medium" color="secondary">
              We use it to generate a one-time code for each showing. Your credentials are stored encrypted.
            </Text>
          </View>

          <CodeboxConnectForm />
        </template>

        <template v-else>
          <View gap="sm" align="center" justify="space-between">
            <View gap="sm" align="center">
              <IconTile icon="key" size="small" colored-icon color="green" />
              <Text variant="body-medium" data-testid="codebox-connected-as">
                Connected as {{ integrationQuery.data.value?.username ?? 'your CodeBox account' }}
              </Text>
            </View>
            <Button variant="ghost" size="small" :disabled="sync.isLoading.value" @click="sync.mutate()">
              <View gap="xs" align="center">
                <Spinner v-if="sync.isLoading.value" size="small" />
                <span>Sync</span>
              </View>
            </Button>
          </View>

          <FormField label="Lockbox for this property" :error="codeboxError">
            <Select v-model="selectedCodeboxId" @blur="touched.codebox = true" data-testid="codebox-select">
              <option :value="null" disabled>Select a lockbox</option>
              <option v-for="codebox in selectableCodeboxes" :key="codebox.id" :value="codebox.id">
                {{ codebox.serialNumber }}{{ codebox.property ? ` — on ${codebox.property.displayName}` : '' }}
              </option>
            </Select>
          </FormField>

          <Hint v-if="!selectableCodeboxes.length">
            No lockboxes found on your CodeBox account. Add one in the CodeBox portal, then hit Sync.
          </Hint>
        </template>
      </View>
    </Card>

    <Card v-else-if="method" size="small" data-testid="manual-access-panel">
      <View direction="column" gap="md">
        <FormField :label="instructionsLabel" :error="instructionsError">
          <Textarea
            v-model="instructions"
            :placeholder="instructionsPlaceholder"
            rows="12"
            @blur="touched.instructions = true"
            data-testid="access-instructions-input"
          />
        </FormField>

        <Hint>Renters get this the moment they confirm the tour, so include the code and where to find it.</Hint>
      </View>
    </Card>
  </View>
</template>

<script setup lang="ts">
import { computed, reactive, ref, useId, watch } from 'vue';

import {
  Button,
  Card,
  FormField,
  Hint,
  IconTile,
  RadioButtonGroup,
  RadioCard,
  Select,
  Spinner,
  Text,
  Textarea,
  View,
} from '@tallo/design-system';
import { SelfGuidedAccessMethod } from '@tallo/property';

import { useCreateAccessInstructions, useUpdateAccessInstructions } from '../../mutations/access-instructions.mutation';
import { useAssignCodebox, useSyncCodeboxes, useUnassignCodebox } from '../../mutations/codebox.mutation';
import { usePropertyAccessInstructionsQuery } from '../../queries/access-instructions.query';
import { useCodeboxIntegrationQuery, useCodeboxesQuery } from '../../queries/codebox.query';
import CodeboxConnectForm from './codebox-connect-form.vue';

const props = defineProps<{ propertyId?: string }>();

// The drawer and the access-instructions modal can be mounted at once, and native radios group by
// name document-wide — a shared name lets one instance uncheck the other's input.
const radioGroupName = `selfGuidedAccessMethod-${useId()}`;
const method = defineModel<SelfGuidedAccessMethod | null>({ required: true });

const accessMethodOptions = [
  {
    value: SelfGuidedAccessMethod.CODEBOX,
    testId: 'codebox',
    title: 'CodeBox lockbox',
    description: 'Tallo generates a fresh code for every showing and sends it to the renter automatically.',
    icon: 'key' as const,
    color: 'green' as const,
  },
  {
    value: SelfGuidedAccessMethod.COMBO_LOCKBOX,
    testId: 'combo',
    title: 'Combination lockbox',
    description: 'A fixed combination you set yourself.',
    icon: 'lock-01' as const,
    color: 'blue' as const,
  },
  {
    value: SelfGuidedAccessMethod.INSTRUCTIONS,
    testId: 'instructions',
    title: 'Written instructions',
    description: 'Free-form directions, e.g. a key under the mat or a door code.',
    icon: 'file-04' as const,
    color: 'yellow' as const,
  },
  {
    value: SelfGuidedAccessMethod.SMART_LOCK,
    testId: 'smart-lock',
    title: 'Smart lock link',
    description: 'A link from your smart-lock provider that the renter opens on arrival.',
    icon: 'link-01' as const,
    color: 'blue' as const,
  },
];

const integrationQuery = useCodeboxIntegrationQuery();
const isConnected = computed(() => Boolean(integrationQuery.data.value?.connected));
const codeboxesQuery = useCodeboxesQuery(() => isConnected.value);

const sync = useSyncCodeboxes();
const assign = useAssignCodebox();
const unassign = useUnassignCodebox();

const codeboxes = computed(() => codeboxesQuery.data.value ?? []);
const assignedCodebox = computed(() => codeboxes.value.find((codebox) => codebox.property?.id === props.propertyId));

// A box already on another property would 409 on assign, so only this property's box and free ones are offered.
const selectableCodeboxes = computed(() =>
  codeboxes.value.filter((codebox) => !codebox.property || codebox.property.id === props.propertyId),
);

const selectedCodeboxId = ref<string | null>(null);

// Tracks the server's answer only, so a refetch that returns the same box never overwrites a pending pick.
const assignedCodeboxId = computed(() => assignedCodebox.value?.id ?? null);

watch(assignedCodeboxId, (codeboxId) => (selectedCodeboxId.value = codeboxId), { immediate: true });

/**
 * The API rejects assigning to a property that still holds a box, so the old one is released first.
 * Both calls are sequential and happen once on save — firing them per selection raced and 400'd.
 */
async function saveCodeboxAssignment() {
  if (!props.propertyId) return;

  const current = assignedCodeboxId.value;

  // Moving to another access method frees the box, which is the only way to put it on another
  // property — assigning a box that is still held elsewhere is rejected.
  if (method.value !== SelfGuidedAccessMethod.CODEBOX) {
    if (!current) return;

    await unassign.mutateAsync({ codeboxId: current });

    return;
  }

  const next = selectedCodeboxId.value;

  if (current === next) return;

  if (current) {
    await unassign.mutateAsync({ codeboxId: current });
  }

  if (next) {
    await assign.mutateAsync({ codeboxId: next, propertyId: props.propertyId });
  }
}

const instructionsCopy: Partial<Record<SelfGuidedAccessMethod, { label: string; placeholder: string }>> = {
  [SelfGuidedAccessMethod.COMBO_LOCKBOX]: {
    label: 'Where the lockbox is, and the combination',
    placeholder:
      'Black lockbox on the gas meter by the side gate. Combination 4417. Please spin the dials after locking up.',
  },
  [SelfGuidedAccessMethod.INSTRUCTIONS]: {
    label: 'How renters let themselves in',
    placeholder: 'Key is under the terracotta pot to the right of the front door. Please put it back before you leave.',
  },
  [SelfGuidedAccessMethod.SMART_LOCK]: {
    label: 'Smart lock link and what to do with it',
    placeholder:
      'Open https://… on your phone when you arrive and tap Unlock. The door re-locks itself after 30 seconds.',
  },
};

const instructionsCopyForMethod = computed(() => (method.value ? instructionsCopy[method.value] : undefined));
const instructionsLabel = computed(() => instructionsCopyForMethod.value?.label ?? 'Access instructions');
const instructionsPlaceholder = computed(() => instructionsCopyForMethod.value?.placeholder ?? '');

const instructionsQuery = usePropertyAccessInstructionsQuery(() => props.propertyId);
const createInstructions = useCreateAccessInstructions();
const updateInstructions = useUpdateAccessInstructions();

const instructions = ref('');

watch(
  () => instructionsQuery.data.value,
  (saved) => (instructions.value = saved?.instructions ?? ''),
  { immediate: true },
);

/**
 * Called by the parent form on save rather than on every keystroke — the endpoint has no upsert,
 * so a create per keystroke would leave a trail of orphan rows.
 */
async function persistInstructions(expiresAt?: string) {
  if (!props.propertyId || method.value === SelfGuidedAccessMethod.CODEBOX) return;

  const text = instructions.value.trim();
  const existing = instructionsQuery.data.value;

  if (!text) return;
  if (text === existing?.instructions && !expiresAt) return;

  if (existing) {
    await updateInstructions.mutateAsync({
      propertyId: props.propertyId,
      instructionId: existing.id,
      instructions: text,
      expiresAt,
    });
  } else {
    await createInstructions.mutateAsync({ propertyId: props.propertyId, instructions: text, expiresAt });
  }
}

/**
 * A self-guided property whose access is half-configured looks fine in the dashboard and then hands
 * the renter nothing at the door, so the incomplete states are blocked at save rather than warned about.
 */
const validationError = computed<string | null>(() => {
  if (!method.value) {
    return 'Choose how renters get in.';
  }

  if (method.value === SelfGuidedAccessMethod.CODEBOX) {
    if (!isConnected.value) {
      return 'Connect your CodeBox account before using a lockbox.';
    }
    if (props.propertyId && !selectedCodeboxId.value) {
      return 'Pick which lockbox is on this property.';
    }
    return null;
  }

  return instructions.value.trim() ? null : 'Add the instructions renters will receive.';
});

const isValid = computed(() => validationError.value === null);

// A field is only wrong once the landlord has had a go at it; flagging it the moment it appears
// reads as the form complaining about work they have not been given a chance to do yet.
const touched = reactive({ codebox: false, instructions: false });

watch(method, () => {
  touched.codebox = false;
  touched.instructions = false;
});

const codeboxError = computed(() =>
  touched.codebox && method.value === SelfGuidedAccessMethod.CODEBOX && isConnected.value
    ? (validationError.value ?? undefined)
    : undefined,
);
const instructionsError = computed(() =>
  touched.instructions && method.value && method.value !== SelfGuidedAccessMethod.CODEBOX
    ? (validationError.value ?? undefined)
    : undefined,
);

/** Everything the landlord changed in this panel, persisted in one go when the parent form saves. */
async function save(expiresAt?: string) {
  await saveCodeboxAssignment();
  await persistInstructions(expiresAt);
}

defineExpose({ save, instructions, isValid, validationError });
</script>
